import { Directive, ElementRef, forwardRef, HostListener, Inject, Input, OnChanges, Optional, Renderer2 } from '@angular/core';
import { COMPOSITION_BUFFER_MODE, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { createTextMaskInputElement } from 'text-mask-core/dist/textMaskCore';

/**
 * Substitui o `angular2-text-mask`, que é distribuído em View Engine e deixa de compilar a partir do Angular 16.
 * Mantém o selector e o input (`[textMask]="{ mask: ..., guide: false }"`) e usa o mesmo motor, o `text-mask-core`.
 */
@Directive({
    selector: '[textMask]',
    exportAs: 'textMask',
    providers: [{
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextMaskDirective),
            multi: true
        }],
    standalone: false
})
export class TextMaskDirective implements ControlValueAccessor, OnChanges {

  @Input('textMask') textMaskConfig: any = {
    mask: [],
    guide: true,
    placeholderChar: '_',
    pipe: undefined,
    keepCharPositions: false,
  };

  private textMaskInputElement: any;
  private inputElement: HTMLInputElement;
  // Evita aplicar a máscara a meio de uma composição de texto (IME)
  private composing = false;

  private onChange = (_: any) => { };
  private onTouched = () => { };

  constructor(
    private renderer: Renderer2,
    private elementRef: ElementRef,
    @Optional() @Inject(COMPOSITION_BUFFER_MODE) private compositionMode: boolean
  ) {
    if (this.compositionMode == null) {
      // No Android os eventos de composição comportam-se de forma diferente do iOS
      this.compositionMode = !/android (\d+)/.test(navigator.userAgent.toLowerCase());
    }
  }

  ngOnChanges(): void {
    this.setupMask(true);
    if (this.textMaskInputElement !== undefined) {
      this.textMaskInputElement.update(this.inputElement.value);
    }
  }

  writeValue(value: any): void {
    this.setupMask();
    this.renderer.setProperty(this.inputElement, 'value', value == null ? '' : value);
    if (this.textMaskInputElement !== undefined) {
      this.textMaskInputElement.update(value);
    }
  }

  registerOnChange(fn: (_: any) => void): void { this.onChange = fn; }

  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.elementRef.nativeElement, 'disabled', isDisabled);
  }

  @HostListener('input', ['$event.target.value'])
  handleInput(value: string): void {
    if (!this.compositionMode || !this.composing) {
      this.setupMask();
      if (this.textMaskInputElement !== undefined) {
        this.textMaskInputElement.update(value);
        this.onChange(this.inputElement.value);
      }
    }
  }

  @HostListener('blur')
  handleBlur(): void {
    this.onTouched();
  }

  @HostListener('compositionstart')
  compositionStart(): void {
    this.composing = true;
  }

  @HostListener('compositionend', ['$event.target.value'])
  compositionEnd(value: string): void {
    this.composing = false;
    if (this.compositionMode) {
      this.handleInput(value);
    }
  }

  private setupMask(create = false): void {
    if (!this.inputElement) {
      const host: HTMLElement = this.elementRef.nativeElement;
      this.inputElement = host.tagName.toUpperCase() === 'INPUT' ? host as HTMLInputElement : host.getElementsByTagName('input')[0];
    }
    if (this.inputElement && create) {
      this.textMaskInputElement = createTextMaskInputElement({ inputElement: this.inputElement, ...this.textMaskConfig });
    }
  }
}
